
import React from "react";
import { useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { GuideUpload } from "@/components/admin/GuideUpload";
import { GuideForm } from "@/components/admin/GuideForm";
import { useGuides } from "@/hooks/use-guides";
import { Plus, Edit, Trash2 } from "lucide-react";
import type { Guide } from "@/types/guides";

export default function AdminGuidePage() {
  const [formOpen, setFormOpen] = useState(false);
  const [editingGuide, setEditingGuide] = useState<Guide | null>(null);
  const { guides, isLoading, createGuide, updateGuide, deleteGuide } = useGuides();

  const handleEdit = (guide: Guide) => {
    setEditingGuide(guide);
    setFormOpen(true);
  };

  const handleCreate = () => {
    setEditingGuide(null);
    setFormOpen(true);
  };

  return (
    <AdminLayout>
      <div className="container max-w-3xl py-16">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">Manage Guides</h1>
          <div className="flex gap-4">
            <Button
              variant="outline"
              onClick={() => window.location.href = '/guide'}
            >
              View Public Page
            </Button>
            <Button
              onClick={handleCreate}
              className="bg-grandpal-green-dark text-white hover:bg-grandpal-green-dark/90"
            >
              <Plus className="mr-2 h-5 w-5" />
              Create Guide
            </Button>
          </div>
        </div>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {guides.map(guide => (
              <Card key={guide.id} className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-grow">
                    <h3 className="font-medium">{guide.title}</h3>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {guide.categories.map((category, index) => (
                        <span 
                          key={index}
                          className="text-xs px-2 py-1 bg-gray-100 rounded-full"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                    {guide.download_url && (
                      <p className="text-sm text-gray-500 mt-2">
                        Current file: {guide.download_url.split('/').pop()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleEdit(guide)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => deleteGuide(guide.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <GuideUpload guide={guide} />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <GuideForm
        open={formOpen}
        onOpenChange={setFormOpen}
        onSubmit={editingGuide ? 
          (data) => updateGuide({ id: editingGuide.id, ...data }) :
          createGuide
        }
        initialData={editingGuide || undefined}
        mode={editingGuide ? 'edit' : 'create'}
      />
    </AdminLayout>
  );
}
